import React, { useState, useEffect } from 'react';
import {
  FiStar, FiMessageSquare, FiTrendingUp, FiFilter, FiSearch,
  FiDownload, FiSend, FiUser, FiCalendar, FiChevronDown,
  FiThumbsUp, FiThumbsDown, FiAlertCircle, FiCheckCircle,
  FiClock, FiBarChart2, FiPieChart, FiActivity
} from 'react-icons/fi';
import { collection, query, where, getDocs, orderBy, limit, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { Line, Doughnut } from 'react-chartjs-2';
import './Feedback.css';

const Feedback = () => {
  const { currentUser, userProfile } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [filteredFeedback, setFilteredFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState('all');

  // Fetch feedback from Firestore
  useEffect(() => {
    const fetchFeedback = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        console.log('[Feedback] Fetching feedback for trainer:', currentUser.uid);

        const feedbackRef = collection(db, 'clientFeedback');
        const feedbackQuery = query(
          feedbackRef,
          where('trainerId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(100)
        );

        const querySnapshot = await getDocs(feedbackQuery);
        const feedbackData = [];

        // Also fetch user data to get client names
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const usersMap = {};
        usersSnapshot.forEach((doc) => {
          usersMap[doc.id] = doc.data();
        });

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const clientData = usersMap[data.userId] || {};

          feedbackData.push({
            id: doc.id,
            clientId: data.userId,
            clientName: clientData.displayName || 'Anonymous Client',
            clientAvatar: clientData.photoURL || null,
            rating: data.rating || 0,
            message: data.feedback || '',
            category: data.category || 'general',
            sentiment: data.sentiment || 'neutral',
            tags: data.tags || [],
            status: data.status || 'unread',
            response: data.response || null,
            responseDate: data.responseDate?.toDate() || null,
            createdAt: data.createdAt?.toDate() || new Date(),
            type: 'feedback'
          });
        });

        console.log('[Feedback] Fetched feedback:', feedbackData.length);
        setFeedbackList(feedbackData);
        setFilteredFeedback(feedbackData);
      } catch (error) {
        console.error('[Feedback] Error fetching feedback:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [currentUser]);

  // Apply filters
  useEffect(() => {
    let filtered = [...feedbackList];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(f =>
        f.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Rating filter
    if (filterRating !== 'all') {
      const ratingNum = parseInt(filterRating);
      filtered = filtered.filter(f => f.rating === ratingNum);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(f => f.status === filterStatus);
    }

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      const ranges = {
        'today': 1,
        'week': 7,
        'month': 30,
        'quarter': 90
      };
      const days = ranges[dateRange];
      if (days) {
        const cutoffDate = new Date(now.setDate(now.getDate() - days));
        filtered = filtered.filter(f => f.createdAt >= cutoffDate);
      }
    }

    // Sort
    filtered.sort((a, b) => {
      switch(sortBy) {
        case 'recent':
          return b.createdAt - a.createdAt;
        case 'oldest':
          return a.createdAt - b.createdAt;
        case 'highest':
          return b.rating - a.rating;
        case 'lowest':
          return a.rating - b.rating;
        default:
          return b.createdAt - a.createdAt;
      }
    });

    setFilteredFeedback(filtered);
  }, [searchQuery, filterRating, filterStatus, sortBy, dateRange, feedbackList]);

  // Calculate statistics
  const stats = {
    total: feedbackList.length,
    unread: feedbackList.filter(f => f.status === 'unread').length,
    responded: feedbackList.filter(f => f.response).length,
    averageRating: feedbackList.length > 0
      ? (feedbackList.reduce((sum, f) => sum + f.rating, 0) / feedbackList.length).toFixed(1)
      : 0,
    positive: feedbackList.filter(f => f.rating >= 4).length,
    neutral: feedbackList.filter(f => f.rating === 3).length,
    negative: feedbackList.filter(f => f.rating <= 2).length,
  };

  // Rating distribution data
  const ratingDistribution = {
    labels: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
    datasets: [{
      label: 'Ratings',
      data: [
        feedbackList.filter(f => f.rating === 1).length,
        feedbackList.filter(f => f.rating === 2).length,
        feedbackList.filter(f => f.rating === 3).length,
        feedbackList.filter(f => f.rating === 4).length,
        feedbackList.filter(f => f.rating === 5).length,
      ],
      backgroundColor: [
        'rgba(239, 68, 68, 0.8)',
        'rgba(249, 115, 22, 0.8)',
        'rgba(234, 179, 8, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(34, 197, 94, 0.9)',
      ],
      borderWidth: 0
    }]
  };

  // Sentiment distribution
  const sentimentData = {
    labels: ['Positive', 'Neutral', 'Negative'],
    datasets: [{
      data: [stats.positive, stats.neutral, stats.negative],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',
        'rgba(234, 179, 8, 0.8)',
        'rgba(239, 68, 68, 0.8)',
      ],
      borderWidth: 0
    }]
  };

  // Calculate trend data from actual feedback (grouped by month)
  const calculateTrendData = () => {
    if (feedbackList.length === 0) {
      return {
        labels: [],
        datasets: [{
          label: 'Average Rating',
          data: [],
          borderColor: 'rgba(255, 107, 53, 1)',
          backgroundColor: 'rgba(255, 107, 53, 0.1)',
          tension: 0.4,
          fill: true
        }]
      };
    }

    // Group feedback by month
    const monthlyData = {};
    feedbackList.forEach(feedback => {
      const monthKey = new Date(feedback.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { total: 0, count: 0 };
      }
      monthlyData[monthKey].total += feedback.rating;
      monthlyData[monthKey].count += 1;
    });

    // Get last 6 months or available months
    const sortedMonths = Object.keys(monthlyData).sort((a, b) => {
      return new Date(a) - new Date(b);
    }).slice(-6);

    const labels = sortedMonths.map(month => month.split(' ')[0]);
    const data = sortedMonths.map(month => {
      const avg = monthlyData[month].total / monthlyData[month].count;
      return parseFloat(avg.toFixed(1));
    });

    return {
      labels,
      datasets: [{
        label: 'Average Rating',
        data,
        borderColor: 'rgba(255, 107, 53, 1)',
        backgroundColor: 'rgba(255, 107, 53, 0.1)',
        tension: 0.4,
        fill: true
      }]
    };
  };

  const trendData = calculateTrendData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(10, 10, 10, 0.95)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ff6b35',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        ticks: { color: 'rgba(255, 255, 255, 0.7)' },
        grid: { display: false }
      },
      y: {
        ticks: { color: 'rgba(255, 255, 255, 0.7)' },
        grid: { color: 'rgba(255, 255, 255, 0.1)' }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          padding: 15,
          font: { size: 12 }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(10, 10, 10, 0.95)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ff6b35',
        borderWidth: 1
      }
    }
  };

  // Handle response submission
  const handleSubmitResponse = async () => {
    if (!responseText.trim() || !selectedFeedback) return;

    setResponding(true);
    try {
      const feedbackRef = doc(db, 'clientFeedback', selectedFeedback.id);
      await updateDoc(feedbackRef, {
        response: responseText,
        responseDate: serverTimestamp(),
        status: 'responded'
      });

      // Update local state
      setFeedbackList(prev => prev.map(f =>
        f.id === selectedFeedback.id
          ? { ...f, response: responseText, responseDate: new Date(), status: 'responded' }
          : f
      ));

      setResponseText('');
      setSelectedFeedback(null);
      alert('Response sent successfully!');
    } catch (error) {
      console.error('[Feedback] Error sending response:', error);
      alert('Error sending response. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  // Mark as read
  const markAsRead = async (feedbackId) => {
    try {
      const feedbackRef = doc(db, 'clientFeedback', feedbackId);
      await updateDoc(feedbackRef, { status: 'read' });

      setFeedbackList(prev => prev.map(f =>
        f.id === feedbackId ? { ...f, status: 'read' } : f
      ));
    } catch (error) {
      console.error('[Feedback] Error marking as read:', error);
    }
  };

  // Export feedback
  const handleExport = () => {
    const csvContent = [
      ['Date', 'Client', 'Rating', 'Message', 'Category', 'Status'],
      ...filteredFeedback.map(f => [
        f.createdAt.toLocaleDateString(),
        f.clientName,
        f.rating,
        f.message.replace(/,/g, ';'),
        f.category,
        f.status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const renderStars = (rating, interactive = false) => {
    return (
      <div className={`star-rating ${interactive ? 'interactive' : ''}`}>
        {[1, 2, 3, 4, 5].map(i => (
          <FiStar key={i} className={i <= rating ? 'filled' : ''} />
        ))}
      </div>
    );
  };

  const getSentimentClass = (rating) => {
    if (rating >= 4) return 'positive';
    if (rating === 3) return 'neutral';
    return 'negative';
  };

  const getSentimentIcon = (rating) => {
    if (rating >= 4) return <FiThumbsUp />;
    if (rating === 3) return <FiActivity />;
    return <FiThumbsDown />;
  };

  if (loading) {
    return (
      <div className="feedback-loading">
        <div className="loading-spinner"></div>
        <p>Loading feedback...</p>
      </div>
    );
  }

  return (
    <div className="feedback-container">
      {/* Header */}
      <div className="feedback-header">
        <div className="header-left">
          <h1>Client Feedback</h1>
          <p className="header-subtitle">
            Manage and respond to client feedback
          </p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleExport}>
            <FiDownload />
            Export
          </button>
          <button
            className="btn-icon"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter />
          </button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="feedback-stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <FiMessageSquare />
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Total Feedback</p>
          </div>
          <div className="stat-trend">
            <FiTrendingUp />
            <span>All time</span>
          </div>
        </div>

        <div className="stat-card rating">
          <div className="stat-icon">
            <FiStar />
          </div>
          <div className="stat-content">
            <h3>{stats.averageRating > 0 ? stats.averageRating : 'N/A'}</h3>
            <p>Average Rating</p>
          </div>
          <div className="stat-rating-visual">
            {renderStars(Math.round(parseFloat(stats.averageRating)))}
          </div>
        </div>

        <div className="stat-card unread">
          <div className="stat-icon">
            <FiAlertCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.unread}</h3>
            <p>Unread</p>
          </div>
          <div className="stat-badge">
            {stats.unread > 0 ? 'Action needed' : 'All caught up'}
          </div>
        </div>

        <div className="stat-card responded">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.responded}</h3>
            <p>Responded</p>
          </div>
          <div className="stat-percentage">
            {stats.total > 0 ? Math.round((stats.responded / stats.total) * 100) : 0}%
          </div>
        </div>
      </div>

      {/* Analytics Section */}
      <div className="feedback-analytics">
        <div className="analytics-card trend-chart">
          <div className="card-header">
            <h3>Rating Trend</h3>
            <div className="header-badge">
              <FiBarChart2 />
              {trendData.labels.length > 0 ? `${trendData.labels.length} months` : 'No data'}
            </div>
          </div>
          <div className="chart-container">
            {trendData.labels.length > 0 ? (
              <Line data={trendData} options={chartOptions} height={200} />
            ) : (
              <div className="chart-empty-state">
                <FiBarChart2 className="empty-chart-icon" />
                <p>No trend data yet</p>
                <span>Ratings will appear here over time</span>
              </div>
            )}
          </div>
        </div>

        <div className="analytics-card distribution-chart">
          <div className="card-header">
            <h3>Rating Distribution</h3>
            <div className="header-badge">
              <FiPieChart />
              All time
            </div>
          </div>
          <div className="chart-container">
            {feedbackList.length > 0 ? (
              <Doughnut data={ratingDistribution} options={doughnutOptions} height={200} />
            ) : (
              <div className="chart-empty-state">
                <FiPieChart className="empty-chart-icon" />
                <p>No distribution data</p>
                <span>Rating breakdown will appear here</span>
              </div>
            )}
          </div>
        </div>

        <div className="analytics-card sentiment-chart">
          <div className="card-header">
            <h3>Sentiment Analysis</h3>
            <div className="header-badge">
              <FiActivity />
              Overview
            </div>
          </div>
          <div className="chart-container">
            {feedbackList.length > 0 ? (
              <Doughnut data={sentimentData} options={doughnutOptions} height={200} />
            ) : (
              <div className="chart-empty-state">
                <FiActivity className="empty-chart-icon" />
                <p>No sentiment data</p>
                <span>Sentiment analysis will appear here</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className={`feedback-controls ${showFilters ? 'expanded' : ''}`}>
        <div className="search-bar">
          <FiSearch />
          <input
            type="text"
            placeholder="Search feedback by client or message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Rating</label>
              <select value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All Status</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="responded">Responded</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Date Range</label>
              <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">Last 3 Months</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="recent">Most Recent</option>
                <option value="oldest">Oldest First</option>
                <option value="highest">Highest Rating</option>
                <option value="lowest">Lowest Rating</option>
              </select>
            </div>
          </div>
        )}

        <div className="results-info">
          Showing <strong>{filteredFeedback.length}</strong> of <strong>{stats.total}</strong> feedback items
        </div>
      </div>

      {/* Feedback List */}
      <div className="feedback-content">
        {filteredFeedback.length === 0 ? (
          <div className="empty-state">
            <FiMessageSquare className="empty-icon" />
            <h3>No Feedback Found</h3>
            <p>
              {searchQuery || filterRating !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters to see more results.'
                : 'You haven\'t received any client feedback yet. Feedback will appear here once clients submit reviews.'}
            </p>
          </div>
        ) : (
          <div className={`feedback-grid ${viewMode}`}>
            {filteredFeedback.map((feedback) => (
              <div
                key={feedback.id}
                className={`feedback-card ${getSentimentClass(feedback.rating)} ${feedback.status}`}
              >
                <div className="feedback-card-header">
                  <div className="client-info">
                    <div className="client-avatar">
                      {feedback.clientAvatar ? (
                        <img src={feedback.clientAvatar} alt={feedback.clientName} />
                      ) : (
                        <div className="avatar-placeholder">
                          <FiUser />
                        </div>
                      )}
                    </div>
                    <div className="client-details">
                      <h4>{feedback.clientName}</h4>
                      <div className="feedback-meta">
                        <span className="feedback-date">
                          <FiCalendar />
                          {feedback.createdAt.toLocaleDateString()}
                        </span>
                        <span className={`status-badge ${feedback.status}`}>
                          {feedback.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="sentiment-indicator">
                    {getSentimentIcon(feedback.rating)}
                  </div>
                </div>

                <div className="feedback-rating">
                  {renderStars(feedback.rating)}
                  <span className="rating-number">{feedback.rating}.0</span>
                </div>

                <div className="feedback-message">
                  <p>{feedback.message}</p>
                </div>

                {feedback.tags && feedback.tags.length > 0 && (
                  <div className="feedback-tags">
                    {feedback.tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                )}

                {feedback.response ? (
                  <div className="feedback-response">
                    <div className="response-header">
                      <strong>Your Response</strong>
                      <span className="response-date">
                        <FiClock />
                        {feedback.responseDate?.toLocaleDateString()}
                      </span>
                    </div>
                    <p>{feedback.response}</p>
                  </div>
                ) : (
                  <div className="feedback-actions">
                    {feedback.status === 'unread' && (
                      <button
                        className="btn-action secondary"
                        onClick={() => markAsRead(feedback.id)}
                      >
                        Mark as Read
                      </button>
                    )}
                    <button
                      className="btn-action primary"
                      onClick={() => setSelectedFeedback(feedback)}
                    >
                      <FiSend />
                      Respond
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Response Modal */}
      {selectedFeedback && (
        <div className="modal-overlay" onClick={() => setSelectedFeedback(null)}>
          <div className="response-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Respond to Feedback</h3>
              <button
                className="modal-close"
                onClick={() => setSelectedFeedback(null)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="original-feedback">
                <div className="original-header">
                  <strong>{selectedFeedback.clientName}</strong>
                  {renderStars(selectedFeedback.rating)}
                </div>
                <p>{selectedFeedback.message}</p>
              </div>

              <div className="response-form">
                <label htmlFor="responseText">Your Response</label>
                <textarea
                  id="responseText"
                  placeholder="Write your response to this feedback..."
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  rows="5"
                />
                <div className="character-count">
                  {responseText.length} characters
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => {
                  setSelectedFeedback(null);
                  setResponseText('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmitResponse}
                disabled={!responseText.trim() || responding}
              >
                {responding ? 'Sending...' : 'Send Response'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
